/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/adjacent-overload-signatures */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  inject,
  Input,
  NgZone,
  PLATFORM_ID,
  TransferState,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import DevExpress from 'devextreme';
import {
  DxDropDownBoxComponent,
  DxTemplateHost,
  IterableDifferHelper,
  NestedOptionHost,
  WatcherHelper,
} from 'devextreme-angular';
import { DxoFilterPanelModule, DxoLoadPanelModule } from 'devextreme-angular/ui/nested';
import { FocusedRowChangedEvent, RowClickEvent, SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { AbcCore, CompareHelper, ConvertHelper, ObjectHelper } from 'libs/abc/core';
import {
  ColumnInfo,
  CustomColumn,
  ObjectKey,
  QueryObjectsModel,
  ReadListDataSourceModel,
  ReadListDataSourceResult,
  ReadPicklistItemsModel,
  TargetColumnValue,
} from 'libs/abc/core/data/src';
import { _AbcGridComponent } from '../abc-grid/abc-grid.component';
import { AbcTooltipDirective } from '../abc-tooltip.directive';
import { AbcCoreComponentService } from '../abcCoreComponent.service';
import {
  AbcDropDownBoxArrayDataConfiguration,
  AbcDropDownBoxPicklistConfiguration,
  AbcDropDownBoxRefColumnConfiguration,
} from './abc-drop-down-box';
import { convertDataType } from '../abc-grid';

@Component({
  selector: 'abc-drop-down-box',
  template: `<abc-grid
    #abcGrid
    (onRowClick)="onRowClick($event)"
    (onFocusedRowChanged)="onFocusedRowChanged($event)"
    (onSelectionChanged)="onSelectionChanged($event)"
    [columns]="gridColumns"
    [keyExpr]="valueExpr"
    [loadPanel]="isLoading"
    [columnAutoWidth]="columnAutoWidth"
    [showColumnHeaders]="!!gridColumns && !hideColumnHeaders"
    twoWayBindingEnabled="false"
  >
    @if (isLoading) {
      <dxo-load-panel [showPane]="false"> </dxo-load-panel>
    }
  </abc-grid>`,
  styleUrls: ['./abc-drop-down-box.component.scss'],
  hostDirectives: [
    {
      directive: AbcTooltipDirective,
    },
  ],
  providers: [
    DxTemplateHost,
    WatcherHelper,
    IterableDifferHelper,
    NestedOptionHost,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AbcDropDownBoxComponent),
    },
  ],
  imports: [_AbcGridComponent, DxoFilterPanelModule, DxoLoadPanelModule],
})
export class AbcDropDownBoxComponent extends DxDropDownBoxComponent implements AfterViewInit {
  private _columns: DevExpress.ui.dxDataGrid.Column[];
  private isDynamic: boolean = false;
  private isInitialized = false;
  private _isPicklist: boolean;
  private readListDataSourceModel: ReadListDataSourceModel;
  private readPicklistItemsModel: ReadPicklistItemsModel;
  private targetColumnsFactory: () => TargetColumnValue[];
  private customColumnsFactory: () => CustomColumn[];
  private customValueExpr: string;
  private customDisplayExpr: string;
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  @Input() public markForCheck: boolean = false;
  @Input() public hideColumnHeaders: boolean = false;
  @Input() public hidePicklistValueColumn: boolean = false;
  @Input() public hideNonCustomColumns: boolean = false;
  @Input() public columnAutoWidth: boolean = undefined;
  @Input() public showClearButtonOnFocusOnly: boolean = undefined;
  // use this if you want to immediately detect selection change on multi-selection (even before the grid gets closed)
  @Input() public immediateValueChange: boolean = false;

  @Input() public selectRowOnClick: boolean = false;

  @Input()
  public override get dataSource(): any[] {
    return <any[]>super.dataSource;
  }
  public override set dataSource(data: any[]) {
    super.dataSource = data;
    this.abcGrid.dataSource = data;
  }

  @Input()
  get gridColumns(): DevExpress.ui.dxDataGrid.Column[] {
    return <DevExpress.ui.dxDataGrid.Column[]>(this._columns ?? this.abcGrid.columns);
  }
  set gridColumns(data: DevExpress.ui.dxDataGrid.Column[]) {
    this._columns = data;
    this.hideColumns();
  }

  private _refColumnConfig: AbcDropDownBoxRefColumnConfiguration;
  @Input()
  public get refColumnConfig(): AbcDropDownBoxRefColumnConfiguration {
    return this._refColumnConfig;
  }

  private settingRefColumnConfig: Promise<void>;
  public set refColumnConfig(config: AbcDropDownBoxRefColumnConfiguration) {
    this.settingRefColumnConfig = this.setRefColumnConfig(config);
  }

  private async setRefColumnConfig(config: AbcDropDownBoxRefColumnConfiguration): Promise<void> {
    this._refColumnConfig = config;
    this._picklistConfig = undefined;
    this._arrayDataConfig = undefined;
    if (config) {
      if (!this.isInitialized && config.objectType && config.refColumnName) {
        await this.setupForRefColumn(
          config.objectType,
          config.refColumnName,
          config.objectId,
          config.initialValue,
          config.initialDescription,
          undefined,
          undefined,
          config.targetColumns ? () => config.targetColumns : undefined,
          config.valueExpr,
          config.displayExpr,
          config.customFilterExpression,
          config.customColumns ? () => config.customColumns : undefined,
        );
      } else {
        await this.updateForRefColumn(
          config.objectId,
          config.initialValue,
          config.initialDescription,
          config.targetColumns ? () => config.targetColumns : undefined,
          config.customFilterExpression,
          config.customColumns ? () => config.customColumns : undefined,
        );
      }
    }
  }

  private _picklistConfig: AbcDropDownBoxPicklistConfiguration;
  @Input()
  public get picklistConfig(): AbcDropDownBoxPicklistConfiguration {
    return this._picklistConfig;
  }

  private settingPicklistConfig: Promise<void>;
  public set picklistConfig(config: AbcDropDownBoxPicklistConfiguration) {
    this.settingPicklistConfig = this.setPicklistConfig(config);
  }

  private async setPicklistConfig(config: AbcDropDownBoxPicklistConfiguration): Promise<void> {
    this._picklistConfig = config;
    this._refColumnConfig = undefined;
    this._arrayDataConfig = undefined;
    if (config) {
      if (!this.isInitialized) {
        await this.setupForPicklistByName(config.picklistName, config.initialValue, config.initialDescription);
      } else {
        await this.updateForPicklistByName(config.initialValue, config.initialDescription);
      }
    }
  }

  private _arrayDataConfig: AbcDropDownBoxArrayDataConfiguration;
  @Input()
  public get arrayDataConfig(): AbcDropDownBoxArrayDataConfiguration {
    return this._arrayDataConfig;
  }

  private settingArrayDataConfig: Promise<void>;
  public set arrayDataConfig(config: AbcDropDownBoxArrayDataConfiguration) {
    this.settingArrayDataConfig = this.setArrayDataConfig(config);
  }

  private async setArrayDataConfig(config: AbcDropDownBoxArrayDataConfiguration): Promise<void> {
    this._arrayDataConfig = config;
    this._refColumnConfig = undefined;
    this._picklistConfig = undefined;
    if (config) {
      let valueExpr = config.valueExpr ?? <string>this.valueExpr;
      let displayExpr = config.displayExpr ?? <string>this.displayExpr;
      if ((!valueExpr || !displayExpr) && config.dataSource?.length > 0) {
        const keys = Object.keys(config.dataSource[0]);
        if (!valueExpr && keys.length > 0) {
          valueExpr = keys[0];
        }
        if (!displayExpr && keys.length > 1) {
          displayExpr = keys[1];
        }
      }
      this.setupForArrayData(config.dataSource, valueExpr, displayExpr, config.gridColumns, config.initialValue);
    }
  }

  public isLoading: boolean = true;
  private openedTimestamp: number;
  private onOpenAction: () => Promise<void>;

  @ViewChild('abcGrid', { static: true }) abcGrid: _AbcGridComponent;

  constructor(
    private elementRef: ElementRef,
    ngZone: NgZone,
    templateHost: DxTemplateHost,
    _watcherHelp: WatcherHelper,
    _idh: IterableDifferHelper,
    optionHost: NestedOptionHost,
    transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: any,
    private abcCoreComponentService: AbcCoreComponentService,
    private abcCore: AbcCore,
    private AbcTooltipDirective: AbcTooltipDirective,
  ) {
    super(elementRef, ngZone, templateHost, _watcherHelp, _idh, optionHost, transferState, platformId);
    this.stylingMode = this.stylingMode ? this.stylingMode : this.abcCoreComponentService.stylingMode;
    this.labelMode = this.labelMode ?? this.abcCoreComponentService.labelMode;

    this.AbcTooltipDirective.elementClassName = '.dx-texteditor-input';

    let isOpen: boolean = false;
    let isReadOnly: boolean = false;
    this.onOpened.subscribe(async (_) => {
      try {
        isReadOnly = this.readOnly;
        if (!isReadOnly) {
          this.readOnly = true;
        }
        this.setClearButtonVisibleIfNecessary(true);
        if (this.onOpenAction) {
          this.isLoading = true;
          await this.onOpenAction();
        }
        if (this.getMultiSelect()) {
          const selectedRows = this.arrayValue;
          if (selectedRows?.length) {
            this.executeDelayed(async () => {
              await this.abcGrid.instance?.navigateToRow(selectedRows[0]);
              await this.abcGrid.instance?.selectRows(selectedRows, false);
              await this.performGridRepaint();
            });
          } else {
            await this.abcGrid.instance?.selectRows(selectedRows, false);
            await this.performGridRepaint();
          }
        } else if (!CompareHelper.isNullorEmpty(this.value)) {
          const v = this.value;
          this.executeDelayed(async () => {
            await this.abcGrid.instance?.navigateToRow(v);
            await this.selectRowsAndNavigate(v);
            await this.performGridRepaint();
            this.executeDelayed(async () => {
              await this.abcGrid.ensureRowVisible(this.value);
            });
          });
        } else {
          this.executeDelayed(async () => {
            this.abcGrid.focusedRowKey = null;
            this.abcGrid.selectedRowKeys = null;
            await this.performGridRepaint();
          });
        }
        isOpen = true;
        this.openedTimestamp = Date.now() + 200;
        this.isLoading = false;

        await this.executeDelayed(async () => {
          this.tryCheckChanges();
          if (!isReadOnly) {
            this.readOnly = false;
          }
        });
      } catch (err) {
        this.abcCore.error.publish(err);
        if (!isReadOnly) {
          this.readOnly = false;
        }
      }
    });

    this.onClosed.subscribe((_) => {
      this.setClearButtonVisibleIfNecessary(false);
      isOpen = false;
      this.openedTimestamp = null;
      if (this.abcGrid) {
        const customValue = this.acceptCustomValue ? this.value : undefined;
        if (!this.getMultiSelect() && this.abcGrid.focusedRowEnabled) {
          this.value = this.abcGrid.focusedRowKey ?? customValue;
        } else if (!CompareHelper.isNullorEmpty(customValue) && !this.abcGrid.selectedRowKeys?.length) {
          this.value = customValue;
        } else {
          this.arrayValue = this.abcGrid.selectedRowKeys;
        }
      }
    });

    this.onValueChanged.subscribe(async (_) => {
      if (isOpen) {
        await this.selectRowsAndNavigate();
      }
    });
  }

  private executeDelayed(action: () => Promise<void>, timeoutMilliseconds: number = 100) {
    setTimeout(async () => {
      try {
        await action();
      } catch (err) {
        console.error(err);
      }
    }, 50);
  }

  private get arrayValue(): any[] {
    const v = this.value;
    const hasValue = !CompareHelper.isNullorEmpty(v);

    if (hasValue) {
      if (Array.isArray(v)) {
        return v;
      } else {
        return [v];
      }
    } else {
      return [];
    }
  }

  private set arrayValue(v: any[]) {
    this.value = v;
  }

  public override get value(): any {
    const v = super.value;
    const hasValue = !CompareHelper.isNullorEmpty(v);
    if (hasValue && Array.isArray(v)) {
      if (v.length === 1) {
        return v[0];
      } else if (v.length === 0) {
        return undefined;
      } else {
        return v;
      }
    }
    if (!hasValue) {
      return undefined;
    }
    return v;
  }

  private currentlySettingValue: any;
  public override set value(v: any) {
    const settingRefColumnConfig = this.settingRefColumnConfig;
    const settingPicklistConfig = this.settingPicklistConfig;
    if (!this.isInitialized && this.currentlySettingValue === v) {
      return;
    }
    if (!this.isInitialized && !CompareHelper.isNullorEmpty(v) && settingRefColumnConfig) {
      this.currentlySettingValue = v;
      this.settingRefColumnConfig = null;
      settingRefColumnConfig.then(async () => {
        try {
          await this.updateForRefColumn(null, v, null, null);
        } finally {
          this.currentlySettingValue = null;
        }
      });
    } else if (!this.isInitialized && !CompareHelper.isNullorEmpty(v) && settingPicklistConfig) {
      this.currentlySettingValue = v;
      this.settingPicklistConfig = null;
      settingPicklistConfig.then(async () => {
        try {
          await this.updateForPicklistByName(v, null);
        } finally {
          this.currentlySettingValue = null;
        }
      });
    } else {
      let hasValue = !CompareHelper.isNullorEmpty(v);
      if (hasValue && Array.isArray(v)) {
        if (v.length === 1) {
          v = v[0];
          hasValue = !CompareHelper.isNullorEmpty(v);
        } else if (v.length === 0) {
          v = null;
          hasValue = false;
        }
      }
      if (!hasValue) {
        v = null;
      }
      const orig = super.value;
      const hasOrigValue = !CompareHelper.isNullorEmpty(orig);
      if (hasOrigValue && hasValue) {
        if (v != orig) {
          super.value = v;
        }
      } else if (hasOrigValue || hasValue) {
        super.value = v;
      } else if (orig !== null) {
        super.value = null;
      }
    }
  }

  public override ngAfterViewInit(): void {
    this.abcCore.objectSpace.ensureInitialized();

    if (this.abcGrid.showColumnHeaders) {
      this.instance.option('dropDownOptions.height', 200);
    }
    this.instance.option('dropDownOptions.minWidth', 300);
    this.instance.option('dropDownOptions.resizeEnabled', true);

    this.setDefaultGridProperties();

    this.instance.endUpdate();

    this.setClearButtonVisibleIfNecessary(false);
  }

  private setClearButtonVisibleIfNecessary(visible: boolean) {
    if (this.showClearButtonOnFocusOnly) {
      if (visible) {
        this.showClearButton = true;
      } else {
        this.showClearButton = false;
      }
    }
  }

  private isColumnsChangeBound: boolean = false;
  public setupForArrayData(
    dataSource: Array<object>,
    valueExpr: string,
    displayExpr: string,
    gridColumns?: DevExpress.ui.dxDataGrid.Column[],
    initialValue?: unknown | unknown[],
    useMultiSelect?: boolean,
  ): void {
    this.valueExpr = valueExpr;
    this.displayExpr = displayExpr;
    this.abcGrid.showColumnHeaders = false;

    if (gridColumns?.length) {
      this._columns = gridColumns;
    } else {
      this.setDefaultGridColumnsAndSettings(displayExpr, valueExpr);
    }
    this.setMultiSelect(useMultiSelect);

    if (this.customValueExpr && dataSource?.length) {
      const valueMap = new Map<unknown, object[]>();
      for (const obj of dataSource) {
        const value = obj[this.customValueExpr];
        let arr = valueMap.get(value);
        if (!arr) {
          arr = [];
          valueMap.set(value, arr);
        }
        arr.push(obj);
      }
      if (valueMap.size !== dataSource.length) {
        const newDataSource: Array<object> = [];
        const values: object = {};
        if (this.readListDataSourceModel?.TargetColumns?.length) {
          for (const tv of this.readListDataSourceModel?.TargetColumns) {
            if (tv.Name !== this.customValueExpr && !CompareHelper.isNullorEmpty(tv.Value)) {
              values[tv.Value.toString()] = tv;
            }
          }
        }
        for (const e of valueMap) {
          const arr = e[1];
          let added = false;
          if (arr.length > 1) {
            this.setDynamic();
            for (const o of arr) {
              for (let value of Object.values(o)) {
                if (!CompareHelper.isNullorEmpty(value) && values[value.toString()]) {
                  newDataSource.push(o);
                  added = true;
                }
                if (added) {
                  break;
                }
              }
              if (added) {
                break;
              }
            }
          }
          if (!added) {
            newDataSource.push(arr[0]);
          }
        }
        dataSource = newDataSource;
      }
    }

    this.dataSource = dataSource;

    if (!CompareHelper.isNullorEmpty(initialValue)) {
      this.setValue(initialValue);
    }

    if (!this.isColumnsChangeBound) {
      this.isColumnsChangeBound = true;
      let isColumnsChanging = false;
      let lastDataSource: any;
      let lastColumnNames: string[];
      this.abcGrid.columnsChange.subscribe(() => {
        if (!isColumnsChanging) {
          isColumnsChanging = true;
          try {
            const columnNames = this.abcGrid.columns
              ?.map((_) => _.dataField)
              ?.sort((v1, v2) => CompareHelper.compare(v1, v2));
            if (lastDataSource != this.dataSource || !CompareHelper.areDeepEqual(lastColumnNames, columnNames)) {
              lastDataSource = this.dataSource;
              lastColumnNames = columnNames;
              this.executeDelayed(async () => {
                this.initializeDefaultFilter();
                this.tryCheckChanges();
              });
            }
          } catch (err) {
            this.abcCore.error.publish(err);
          } finally {
            isColumnsChanging = false;
          }
        }
      });
    }

    const visibleColumnsCount: number = this.hideColumns();
    this.updateColumnHeaders(visibleColumnsCount, useMultiSelect);
  }

  public async setupForRefColumn(
    objectType: string | number,
    columnName: string,
    objectId?: string,
    initialValue?: string | string[],
    initialDescription?: string | string[],
    useMultiSelect?: boolean,
    contextFilterExpression?: string,
    targetColumnsFactory?: () => TargetColumnValue[],
    valueExpr?: string,
    displayExpr?: string,
    customFilterExpression?: string,
    customColumnsFactory?: () => CustomColumn[],
  ): Promise<void> {
    this.customValueExpr = valueExpr;
    this.customDisplayExpr = displayExpr;
    if (!valueExpr || !displayExpr) {
      let defaultValueExpr: string;
      let defaultDisplayExpr: string;
      if (await this.isPicklist(objectType, columnName)) {
        this.columnAutoWidth = true;
        defaultValueExpr = 'Value';
        defaultDisplayExpr = 'Description';
      } else {
        defaultValueExpr = 'Id';
        defaultDisplayExpr = 'Name';
      }
      if (!valueExpr) {
        valueExpr = defaultValueExpr;
      }
      if (!displayExpr) {
        displayExpr = defaultDisplayExpr;
      }
    }

    this.setupForArrayData([], valueExpr, displayExpr);
    this.isInitialized = false;

    this.readListDataSourceModel = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = <string>objectType;
    this.readListDataSourceModel.ColumnName = columnName;
    this.readListDataSourceModel.ObjectKey = objectKey;

    if (objectId) {
      this.readListDataSourceModel.ObjectKey.Id = objectId;
    }

    if (contextFilterExpression) {
      this.readListDataSourceModel.ContextFilterExpression = contextFilterExpression;
    }

    if (customFilterExpression) {
      this.readListDataSourceModel.CustomFilterExpression = customFilterExpression;
    }

    this.targetColumnsFactory = targetColumnsFactory;
    this.customColumnsFactory = customColumnsFactory;

    let isDataSourceSet = false;
    await this.setInitialValuesWithAwait(
      valueExpr,
      displayExpr,
      initialValue,
      initialDescription,
      useMultiSelect,
      () => {
        isDataSourceSet = true;
        return this.setListDataSource(
          this.readListDataSourceModel,
          this.targetColumnsFactory,
          this.customColumnsFactory,
          initialValue,
          useMultiSelect,
        );
      },
    );

    this.onOpenAction = async () => {
      if (!isDataSourceSet) {
        await this.setListDataSource(
          this.readListDataSourceModel,
          this.targetColumnsFactory,
          this.customColumnsFactory,
          initialValue,
          useMultiSelect,
        );
      }
      isDataSourceSet = false;
    };
  }

  public async updateForRefColumn(
    objectId: string,
    initialValue?: string | string[],
    initialDescription?: string | string[],
    targetColumnsFactory?: () => TargetColumnValue[],
    customFilterExpression?: string,
    customColumnsFactory?: () => CustomColumn[],
  ) {
    if (objectId) {
      this.readListDataSourceModel.ObjectKey.Id = objectId;
    }

    if (customFilterExpression !== undefined) {
      this.readListDataSourceModel.CustomFilterExpression = customFilterExpression;
    }

    if (targetColumnsFactory) {
      this.targetColumnsFactory = targetColumnsFactory;
    }

    if (customColumnsFactory) {
      this.customColumnsFactory = customColumnsFactory;
    }

    if (
      !this.isInitialized ||
      !this.isDynamic ||
      this.updateReadListDataSourceModel(this.readListDataSourceModel, targetColumnsFactory, customColumnsFactory, true)
    ) {
      await this.setInitialValuesWithAwait(
        <string>this.valueExpr,
        <string>this.displayExpr,
        initialValue,
        initialDescription,
        this.getMultiSelect(),
        () => {
          return this.setListDataSource(
            this.readListDataSourceModel,
            this.targetColumnsFactory,
            this.customColumnsFactory,
            initialValue,
            this.getMultiSelect(),
          );
        },
      );
      this.lastReadListDataSourceModel = null;
    } else if (initialValue !== this.value) {
      await this.selectRowsAndNavigate(initialValue);
    }
  }

  public async setupForPicklistByName(
    picklistName: string,
    initialValue?: string | string[],
    initialDescription?: string | string[],
    useMultiSelect?: boolean,
  ): Promise<void> {
    const valueExpr = 'Value';
    const displayExpr = 'Description';

    this.setupForArrayData([], valueExpr, displayExpr);
    this.isInitialized = false;
    this.columnAutoWidth = true;

    this.readPicklistItemsModel = new ReadPicklistItemsModel();
    this.readPicklistItemsModel.Name = picklistName;

    let isDataSourceSet = false;
    await this.setInitialValuesWithAwait(
      valueExpr,
      displayExpr,
      initialValue,
      initialDescription,
      useMultiSelect,
      () => {
        isDataSourceSet = true;
        return this.setPicklistDataSource(
          this.readPicklistItemsModel,
          valueExpr,
          displayExpr,
          initialValue,
          useMultiSelect,
        );
      },
    );

    this.onOpenAction = async () => {
      if (!isDataSourceSet) {
        await this.setPicklistDataSource(
          this.readPicklistItemsModel,
          valueExpr,
          displayExpr,
          initialValue,
          useMultiSelect,
        );
      }
      isDataSourceSet = false;
    };
  }

  public async updateForPicklistByName(initialValue?: unknown | unknown[], initialDescription?: string | string[]) {
    await this.setInitialValuesWithAwait(
      <string>this.valueExpr,
      <string>this.displayExpr,
      initialValue,
      initialDescription,
      this.getMultiSelect(),
      () => {
        return this.setPicklistDataSource(
          this.readPicklistItemsModel,
          <string>this.valueExpr,
          <string>this.displayExpr,
          initialValue,
          this.getMultiSelect(),
        );
      },
    );
  }

  public async setupForQuery(
    queryObjectsModel: QueryObjectsModel,
    valueExpr: string,
    displayExpr: string,
    gridColumns?: DevExpress.ui.dxDataGrid.Column[],
    initialValue?: unknown | unknown[],
    initialDescription?: string | string[],
    useMultiSelect?: boolean,
  ): Promise<void> {
    this.setupForArrayData([], valueExpr, displayExpr, gridColumns);
    this.isInitialized = false;

    let isDataSourceSet = false;

    await this.setInitialValuesWithAwait(
      valueExpr,
      displayExpr,
      initialValue,
      initialDescription,
      useMultiSelect,
      () => {
        isDataSourceSet = true;
        return this.setQueryDataSource(
          queryObjectsModel,
          valueExpr,
          displayExpr,
          gridColumns,
          initialValue,
          useMultiSelect,
        );
      },
    );

    this.onOpenAction = async () => {
      if (!isDataSourceSet) {
        await this.setQueryDataSource(
          queryObjectsModel,
          valueExpr,
          displayExpr,
          gridColumns,
          initialValue,
          useMultiSelect,
        );
      }
      isDataSourceSet = false;
    };
  }

  public async onRowClick(event: RowClickEvent): Promise<void> {
    if (!this.getMultiSelect()) {
      await this.applyGridSelection(true, event?.data);
    } else if (this.selectRowOnClick) {
      await this.toggleRowSelection(event);
    }
  }
  public async onFocusedRowChanged(event: FocusedRowChangedEvent): Promise<void> {
    if (!this.getMultiSelect() && Date.now() - this.openedTimestamp > 0) {
      await this.applyGridSelection(false);
    }
  }

  public async onSelectionChanged(event: SelectionChangedEvent): Promise<void> {
    if (this.immediateValueChange && this.getMultiSelect()) {
      this.setValue(event.selectedRowsData.map((_) => _[<string>this.valueExpr]));
    }
  }

  private async toggleRowSelection(event: RowClickEvent): Promise<void> {
    if (event?.rowType !== 'data' || CompareHelper.isNullorEmpty(event.key)) {
      return;
    }
    // to avoide double toggle
    const clickedElement = event.event?.target as HTMLElement | null;
    if (clickedElement?.closest('.dx-command-select')) {
      return;
    }
    const key: unknown = event.key;
    const grid = event.component;
    if (grid.isRowSelected(key)) {
      await grid.deselectRows([key]);
    } else {
      await grid.selectRows([key], true);
    }
  }
  private async applyGridSelection(forceClose: boolean, rowData?: any): Promise<void> {
    const data = rowData ?? (await this.abcGrid.getFocusedRowData());
    const value = data ? data[<string>this.valueExpr] : undefined;
    const close = forceClose || value != this.value;
    if (!CompareHelper.isNullorEmpty(value)) {
      this.selectRowsAndNavigate(value);
    }
    if (close) {
      this.closeGridBox();
    }
  }

  private setValue(value: unknown | unknown[]): void {
    this.value = value;
  }

  private createInitialItem(value: unknown, displayText: string, valueExpr: string, displayExpr: string): Object {
    const item: Object = new Object();
    item[valueExpr] = value;
    item[displayExpr] = displayText;
    return item;
  }

  private async setInitialValuesWithAwait(
    valueExpr: string,
    displayExpr: string,
    initialValue: unknown | unknown[],
    initialDescription: string | string[],
    useMultiSelect: boolean,
    callback: () => Promise<void>,
  ): Promise<void> {
    try {
      if (
        (this.isInitialized && !this.isDynamic) ||
        CompareHelper.isNullorEmpty(initialValue) ||
        !this.updateReadListDataSourceModel(
          this.readListDataSourceModel,
          this.targetColumnsFactory,
          this.customColumnsFactory,
        ) ||
        this.isInCurrentDataSource(initialValue, initialDescription)
      ) {
        this.setValue(initialValue);
      } else {
        let dataSource: Array<Object> = [];
        if (initialDescription) {
          if (Array.isArray(initialValue)) {
            for (let index = 0; index < initialValue.length; index++) {
              const initialData = this.createInitialItem(
                initialValue[index],
                initialDescription[index],
                valueExpr,
                displayExpr,
              );

              dataSource.push(initialData);
            }
          } else {
            const initialData = this.createInitialItem(
              initialValue,
              <string>initialDescription,
              valueExpr,
              displayExpr,
            );

            dataSource.push(initialData);
            if (!this.isDynamic) {
              this.displayValueFormatter = (value: string | any[]) => {
                if (
                  (!value || (Array.isArray(value) && (!value?.length || (value.length === 1 && !value[0])))) &&
                  this.value == initialValue
                ) {
                  if (Array.isArray(initialDescription)) {
                    return initialDescription.join(', ');
                  } else {
                    return initialDescription;
                  }
                } else if (Array.isArray(value)) {
                  return value.join(', ');
                } else {
                  return value ?? '';
                }
              };
            }
          }
          this.setupForArrayData(dataSource, valueExpr, displayExpr, null, initialValue, useMultiSelect);
        } else {
          const readOnly = this.readOnly;
          let isWaiting = true;
          try {
            if (!this.readOnly) {
              this.readOnly = true;
            }
            if (initialValue) {
              const v = Array.isArray(initialValue) ? initialValue[0] : initialValue;
              if (v && !this.isDynamic) {
                this.displayValueFormatter = (value: string | any[]) => {
                  if (isWaiting) {
                    return '...';
                  } else if (Array.isArray(value)) {
                    return value.join(', ');
                  } else {
                    return value ?? '';
                  }
                };
              }
            }
            await callback();
          } finally {
            isWaiting = false;
            if (this.readOnly !== readOnly) {
              this.readOnly = readOnly;
            }
            await this.performRepaint();
          }
        }
      }
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  private tryCheckChanges(): boolean {
    if (this.markForCheck) {
      this.cdr.markForCheck();
      return true;
    }
    return false;
  }

  private isGridRepainting: boolean;
  private async performGridRepaint(): Promise<void> {
    if (!this.isGridRepainting) {
      this.isGridRepainting = true;
      await this.executeDelayed(async () => {
        try {
          this.abcGrid?.instance?.repaint();
        } finally {
          this.isGridRepainting = false;
        }
      });
    }
  }

  private isRepainting: boolean;
  private async performRepaint(): Promise<void> {
    if (!this.isRepainting) {
      this.isRepainting = true;
      await this.executeDelayed(async () => {
        try {
          this.instance?.repaint();
        } finally {
          this.isRepainting = false;
        }
      });
    }
  }

  private isInCurrentDataSource(value: unknown | unknown[], text: string | string[]): boolean {
    if (this.dataSource && value && text) {
      let arrvalues: unknown[];
      if (!Array.isArray(value)) {
        arrvalues = [value];
      } else {
        arrvalues = value;
      }
      let arrtexts: unknown[];
      if (!Array.isArray(text)) {
        arrtexts = [text];
      } else {
        arrtexts = text;
      }
      for (let i = 0; i < arrvalues.length && i < arrtexts.length; i++) {
        const v = arrvalues[i];
        const t = arrtexts[i];
        for (const row of this.dataSource) {
          if (row[<string>this.valueExpr] == v && row[<string>this.displayExpr] == t) {
            return true;
          }
        }
      }
      return false;
    }
    return false;
  }

  private lastReadListDataSourceModel: string;
  private updateReadListDataSourceModel(
    readListDataSourceModel: ReadListDataSourceModel,
    targetColumnsFactory: () => TargetColumnValue[],
    customColumnsFactory: () => CustomColumn[],
    checkOnly?: boolean,
  ) {
    if (readListDataSourceModel && targetColumnsFactory) {
      readListDataSourceModel.TargetColumns = targetColumnsFactory();
    }
    if (readListDataSourceModel && customColumnsFactory) {
      readListDataSourceModel.CustomColumns = customColumnsFactory();
    }
    let refreshData: boolean = true;
    if (this.isDynamic) {
      const m = readListDataSourceModel;
      const i = m?.TargetColumns?.findIndex((_) => _.Name === readListDataSourceModel.ColumnName);
      if (i >= 0) {
        m.TargetColumns.splice(i, 1);
      }
      const str: string = JSON.stringify(m);
      if (str === this.lastReadListDataSourceModel) {
        refreshData = false;
      }
      if (!checkOnly) {
        this.lastReadListDataSourceModel = str;
      }
    }
    return refreshData;
  }

  private async setListDataSource(
    readListDataSourceModel: ReadListDataSourceModel,
    targetColumnsFactory: () => TargetColumnValue[],
    customColumnsFactory: () => CustomColumn[],
    initialValue: string | string[],
    useMultiSelect: boolean,
  ): Promise<void> {
    if (!this.isInitialized || this.isDynamic) {
      const refreshData: boolean = this.updateReadListDataSourceModel(
        readListDataSourceModel,
        targetColumnsFactory,
        customColumnsFactory,
      );
      if (refreshData) {
        this.dataSource = [];

        const gridObjects: ReadListDataSourceResult = ConvertHelper.arrayBufferToObject(
          await this.abcCore.query.readListDataSource(readListDataSourceModel).toPromise(),
        );

        if (gridObjects) {
          this.isDynamic = ConvertHelper.toBoolean(gridObjects.IsDynamic);

          if (this._isPicklist) {
            this.localizePicklistColumns(gridObjects.ColumnInfos);
          }

          this.setupForArrayData(
            gridObjects.Data,
            this.customValueExpr ?? this.getValueExpr(gridObjects.ColumnInfos),
            this.customDisplayExpr ?? this.getDisplayExpr(gridObjects.ColumnInfos),
            this.createGridColumns(gridObjects),
            initialValue,
            useMultiSelect,
          );
        }
        if (!this.isInitialized && !CompareHelper.isNullorEmpty(initialValue)) {
          await this.selectRowsAndNavigate(initialValue);
        } else {
          await this.selectRowsAndNavigate();
        }

        this.isInitialized = true;
      }
    }
  }

  private async setPicklistDataSource(
    readPicklistItemsModel: ReadPicklistItemsModel,
    valueExpr: string,
    displayExpr: string,
    initialValue: unknown | unknown[],
    useMultiSelect: boolean,
  ): Promise<void> {
    if (!this.isInitialized || this.isDynamic) {
      this.dataSource = [];
      const picklistItems: { ColumnInfos: []; Data: [] } = ConvertHelper.arrayBufferToObject(
        await this.abcCore.data.executeReadPicklistsItemsQuery(readPicklistItemsModel).toPromise(),
      );

      this.setupForArrayData(picklistItems.Data, valueExpr, displayExpr, null, initialValue, useMultiSelect);

      if (!this.isInitialized && !CompareHelper.isNullorEmpty(initialValue)) {
        await this.selectRowsAndNavigate(initialValue);
      } else {
        await this.selectRowsAndNavigate();
      }

      this.isInitialized = true;
    }
  }

  private async setQueryDataSource(
    queryObjectsModel: QueryObjectsModel,
    valueExpr: string,
    displayExpr: string,
    gridColumns: DevExpress.ui.dxDataGrid.Column[],
    initialValue: unknown | unknown[],
    useMultiSelect?: boolean,
  ): Promise<void> {
    if (!this.isInitialized || this.isDynamic) {
      this.dataSource = [];
      const gridObjects: object[] = ConvertHelper.arrayBufferToObjectArray(
        await this.abcCore.query.readObjects(queryObjectsModel).toPromise(),
      );

      this.setupForArrayData(gridObjects, valueExpr, displayExpr, gridColumns, initialValue, useMultiSelect);

      if (!this.isInitialized && !CompareHelper.isNullorEmpty(initialValue)) {
        await this.selectRowsAndNavigate(initialValue);
      } else {
        await this.selectRowsAndNavigate();
      }

      this.isInitialized = true;
    }
  }

  private createGridColumns(dataSource: ReadListDataSourceResult): DevExpress.ui.dxDataGrid.Column[] {
    const columnInfos: ColumnInfo[] = dataSource.ColumnInfos;
    const gridColumns: DevExpress.ui.dxDataGrid.Column[] = [];
    for (const columnInfo of columnInfos) {
      gridColumns.push({
        dataField: columnInfo.Name,
        caption: columnInfo.Caption,
        name: columnInfo.Name,
        dataType: convertDataType(columnInfo.DataType),
      });
    }
    if (this._isPicklist) {
      gridColumns.sort((_, __) => {
        if (_.name === 'Value') {
          return -1;
        }
        if (__.name === 'Value') {
          1;
        }
        return 0;
      });
    }
    return gridColumns;
  }

  private updateColumnHeaders(visibleColumnsCount: number, useMultiSelect: boolean) {
    if (useMultiSelect) {
      this.hideColumnHeaders = false;
    } else if (visibleColumnsCount > 1) {
      if (this.gridColumns.find((column) => column.dataField === 'Id') && !this._isPicklist) {
        this.hideColumnHeaders = false;
      } else if (visibleColumnsCount > 2) {
        this.hideColumnHeaders = false;
      } else {
        this.hideColumnHeaders = true;
      }
    } else {
      this.hideColumnHeaders = true;
    }

    this.tryCheckChanges();
  }

  private localizePicklistColumns(columnInfos: ColumnInfo[]): void {
    for (let index = 0; index < columnInfos.length; index++) {
      const column = columnInfos[index];
      if (column) {
        if (column.Name === 'Value') {
          column.Caption = this.abcGrid?.res?.get('Picklist-Value') ?? column.Caption;
        } else if (column.Name === 'Description') {
          column.Caption = this.abcGrid?.res?.get('Picklist-Description') ?? column.Caption;
        }
      }
    }
  }

  private setMultiSelect(useMultiSelect: boolean): void {
    if (useMultiSelect) {
      this.abcGrid.selection = {
        mode: 'multiple',
        allowSelectAll: true,
        showCheckBoxesMode: 'always',
      };
    } else {
      this.abcGrid.selection = { mode: 'single' };
    }
  }

  private getMultiSelect(): boolean {
    return this.abcGrid?.selection?.mode === 'multiple';
  }

  private async isPicklist(objectType: string | number, columnName: string): Promise<boolean> {
    const objectTypeViewModel = await this.abcCore.objectSpace.getObjectType(objectType).toPromise();
    const column = objectTypeViewModel?.columnsByName?.get(columnName);
    if (column?.columnType === 'IdRef') {
      this._isPicklist = false;
      return false;
    } else {
      this._isPicklist = true;
      return true;
    }
  }

  private async selectRowsAndNavigate(newValue?: unknown | unknown[]): Promise<void> {
    if (this.abcGrid && this.dataSource?.length && !this.immediateValueChange) {
      if (this.getMultiSelect()) {
        if (this.abcGrid.focusedRowEnabled) {
          this.abcGrid.focusedRowEnabled = false;
          this.abcGrid.autoNavigateToFocusedRow = false;
        }
        let selectedRows = this.arrayValue;
        if (!CompareHelper.isNullorEmpty(newValue)) {
          if (Array.isArray(newValue)) {
            selectedRows = newValue;
          } else {
            selectedRows = [newValue];
          }
        }
        this.abcGrid.selectedRowKeys = selectedRows;
        this.abcGrid.focusedRowKey = null;
        if (!CompareHelper.areDeepEqual(this.value, selectedRows, true)) {
          this.instance?.option('value', selectedRows);
        }
      } else {
        const value = newValue ?? this.value;
        const foundEntry = this.dataSource?.find((_) => _[<string>this.valueExpr] == value);
        const foundValue = foundEntry ? foundEntry[<string>this.valueExpr] : undefined;
        const hasFoundValue = !CompareHelper.isNullorEmpty(foundValue);
        if (!this.abcGrid.focusedRowEnabled && hasFoundValue) {
          this.abcGrid.focusedRowEnabled = true;
          this.abcGrid.autoNavigateToFocusedRow = true;
        } else if (this.abcGrid.focusedRowEnabled && !hasFoundValue) {
          this.abcGrid.focusedRowEnabled = false;
          this.abcGrid.autoNavigateToFocusedRow = false;
        }
        if (this.abcGrid.focusedRowEnabled && hasFoundValue) {
          this.abcGrid.focusedRowKey = foundValue;
          this.abcGrid.selectedRowKeys = [foundValue];
        } else {
          this.abcGrid.focusedRowKey = null;
          this.abcGrid.selectedRowKeys = [];
        }
        if (this.value !== foundValue && (!this.acceptCustomValue || !CompareHelper.isNullorEmpty(foundValue))) {
          this.instance?.option('value', foundValue);
        }
      }
    }
  }

  private getValueExpr(columnInfos: ColumnInfo[]): string {
    const candidates: string[] = ['Value', 'Id', 'Identifier'];
    const column: ColumnInfo = this.findFirstColumnByName(columnInfos, candidates);

    if (column) {
      return column.Name;
    } else {
      return columnInfos[columnInfos.length - 1].Name;
    }
  }

  private getDisplayExpr(columnInfos: ColumnInfo[]): string {
    const candidates: string[] = [
      'Description',
      'DisplayText___HIDDEN',
      'Name',
      'Caption',
      'Bezeichnung',
      'Designation',
      'Identifier',
    ];
    const column: ColumnInfo = this.findFirstColumnByName(columnInfos, candidates);

    if (column) {
      return column.Name;
    } else {
      return columnInfos[0].Name;
    }
  }

  private findFirstColumnByName(columnInfos: ColumnInfo[], candidates: string[]): ColumnInfo {
    for (const candidate of candidates) {
      const columnInfo: ColumnInfo = columnInfos.find((columnInfo) => columnInfo.Name === candidate);

      if (columnInfo) {
        return columnInfo;
      }
    }
    return undefined;
  }

  private setDefaultGridColumnsAndSettings(displayExpr: string, valueExpr: string): void {
    this._columns = [
      {
        name: valueExpr,
        dataField: valueExpr,
        caption: valueExpr === 'Value' ? (this.abcGrid.res.get('Picklist-Value') ?? valueExpr) : valueExpr,
      },
    ];
    if (valueExpr !== displayExpr) {
      this._columns.push({
        name: displayExpr,
        dataField: displayExpr,
        caption:
          displayExpr === 'Description' ? (this.abcGrid.res.get('Picklist-Description') ?? displayExpr) : displayExpr,
      });
    }
    this.hideColumnHeaders = true;
  }

  private closeGridBox() {
    this.opened = false;
  }

  private setDefaultGridProperties() {
    this.abcGrid.height = '100%';
    this.abcGrid.allowColumnResizing = true;
    this.abcGrid.scrolling = { mode: 'virtual' };
    this.abcGrid.hoverStateEnabled = true;
    this.abcGrid.filterRow = { visible: true };
    this.abcGrid.loadPanel.enabled = true;
    if (this.abcGrid.showColumnHeaders) {
      this.abcGrid.headerFilter = { visible: true };
    }
    if (this.acceptCustomValue) {
      this.openOnFieldClick = false;
    }
  }

  private hideColumns(): number {
    let visibleColumnsCount: number = 0;
    if (this.gridColumns) {
      for (const element of this.gridColumns) {
        if (ObjectHelper.isObject(element)) {
          element.visible = true;
          if (
            element.dataField === 'Id' ||
            element.dataField === 'id' ||
            element.dataField?.endsWith('___HIDDEN') ||
            element.dataField === 'ObjectType' ||
            element.dataField === 'ParentObjectType' ||
            element.dataField === '__DefaultSort' ||
            element.dataField === '__DefaultFilter'
          ) {
            element.visible = false;
            element.showInColumnChooser = false;
          }
          if (element.dataField === '__DefaultSort') {
            element.sortOrder = 'asc';
          }
          if (this.hidePicklistValueColumn && element.dataField === 'Value') {
            element.visible = false;
          }
          if (this.hideNonCustomColumns && this.readListDataSourceModel?.CustomColumns?.length) {
            if (!this.readListDataSourceModel.CustomColumns.find((cc) => cc.Name === element.dataField)) {
              element.visible = false;
            }
          }
          if (this._isPicklist) {
            const candidates = ['SortIndex', 'Identifier', 'Id'];
            if (candidates.includes(element.name)) {
              element.visible = false;
            }
          }
          if (element.visible) {
            visibleColumnsCount++;
          }
        }
      }
    }

    return visibleColumnsCount;
  }

  private initializeDefaultFilter(): boolean {
    if (this.abcGrid?.instance?.clearFilter) {
      this.abcGrid?.instance?.clearFilter();
    }
    if (this.abcGrid?.columns) {
      this.abcGrid.filterValue = null;
      for (const column of this.abcGrid.columns) {
        if (column?.dataField === '__DefaultFilter') {
          if (this.abcGrid.filterPanel) {
            this.abcGrid.filterPanel.visible = true;
          }
          column.visible = false;
          const defaultFilter = [column.dataField, '=', '1'];
          this.abcGrid.filterValue = defaultFilter;
          if (this._isPicklist) {
            this.abcGrid.setCustomFilterText(defaultFilter, false, this.abcGrid.res.get('Picklist-DefaultFilter'));
          }
          return true;
        }
      }
    }
    return false;
  }

  private setDynamic(): void {
    this.isDynamic = true;
    this.lastReadListDataSourceModel = null;
  }
}
